import { saveWorksheet } from "../../data/worksheetsApi";
import React, { useEffect, useRef, useState } from "react";
import { CloudDoneOutlined, CloudUploadOutlined, Close, ErrorOutline, ExpandLess, ExpandMore, PictureAsPdf, Refresh, SaveOutlined, WarningAmberOutlined } from "@mui/icons-material";
import { Tooltip } from "@mui/material";
import "./Texteditor.css"
import "./ColumnOps.css"

import {
  PageNode,
  HeaderFooterNode,
  BodyNode,
} from 'tiptap-extension-pagination'


import { MathInline } from "./helpers/mathFormatter";


//tip-tap
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { TextStyle } from "@tiptap/extension-text-style";
import { Extension } from "@tiptap/core";
import TextAlign from '@tiptap/extension-text-align'
import Color from '@tiptap/extension-color'
import Subscript from '@tiptap/extension-subscript'
import Superscript from '@tiptap/extension-superscript'
import Underline from '@tiptap/extension-underline'
import Table from '@tiptap/extension-table'
import TableRow from '@tiptap/extension-table-row'
import TableCell from '@tiptap/extension-table-cell'
import TableHeader from '@tiptap/extension-table-header'



//comps
import { MenuBar } from "./MenuBar/MenuBar";
import { MathEditModal } from "./MathEditModal/MathEditModal";
import PdfExportModal from "./PdfExportModal/PdfExportModal";
import EditorHelpModal from "./MenuBar/EditorHelpModal/EditorHelpModal";

//helpers

import { AnswerBox } from "./helpers/tiptapNodes";
import { Diagram } from "./helpers/diagrams/diagramNode";


import { buildDocFromTemplate, upgradeExamLayout } from "./tiptapTemplateBuilders";



import FeedbackModal from "../FeedbackModal/FeedbackModel"
import GenerationFeedbackToast from "../GenerationFeedbackToast/GenerationFeedbackToast"

import { useAuth } from "../Login/AuthContext";
import { markGenerationExported } from "../../data/generationsApi";
import { newRequestId } from "../../data/requestId";
import CustomPrompt from "../CustomPrompt/CustomPrompt";
import { regenerateWorksheet } from "../../data/regenerateWorksheet";
import { useWorksheetDraft } from "../../customhooks/useWorksheetDraft";
import { QuestionBlock, QuestionRow, AnswerSection, WorksheetHeading } from "./helpers/worksheetBlocks";
import { WorksheetPagination } from "./helpers/worksheetPagination";
import { FIRST_WORKSHEET_KEY } from "../MobilePopUp/Popup";


const ExtendedTable = Table.extend({
  addAttributes() {
    return {
      columnMath: {
        default: false,
        parseHTML: element =>
          element.getAttribute('data-column-math') === 'true',
        renderHTML: attrs => {
          if (!attrs.columnMath) {
            return {}
          }
          return { 'data-column-math': 'true' }
        },
      },
    }
  },
})



const FontSize = Extension.create({
  name: "fontSize",

  addOptions() {
    return {
      types: ["textStyle"],
    };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontSize: {
            default: null,
            parseHTML: element => element.style.fontSize.replace(/['"]+/g, ""),
            renderHTML: attributes => {
              if (!attributes.fontSize) {
                return {};
              }
              return {
                style: `font-size: ${attributes.fontSize}`,
              };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      setFontSize: fontSize => ({ chain }) => {
        return chain().setMark("textStyle", { fontSize }).run();
      },
      unsetFontSize: () => ({ chain }) => {
        return chain()
          .setMark("textStyle", { fontSize: null })
          .removeEmptyTextStyle()
          .run();
      },
    };
  },
});



export default function TextEditor({
  questions = [],
  template_id = "",
  initialContent = null,
  worksheetId = null,
  worksheetTitle = "",
  sourcePrompt = null,
  onWorksheetPersisted = null,
  generationId = null,
  generationQuality = null,
  generationSettings = null,
  onVersionCreated = null,
}) {

  const [toolsExpanded, setToolsExpanded] = useState(false);
  const [dismissedQualityNotice, setDismissedQualityNotice] = useState(null);
  const toolsToggleRef = useRef(null);
  const regenerateToggleRef = useRef(null);
  const regeneratePanelRef = useRef(null);
  const [showRegenerate, setShowRegenerate] = useState(false);
  const closeRegenerate = () => { setShowRegenerate(false); regenerateToggleRef.current?.focus(); };
  useEffect(() => { if (showRegenerate) regeneratePanelRef.current?.focus(); }, [showRegenerate]);
  const [generationStage,setGenerationStage]=useState('Generating questions');
  const [regenerating, setRegenerating] = useState(false);
  const [regenerateError, setRegenerateError] = useState('');
  const [title, setTitle] = useState(worksheetTitle || 'Untitled worksheet');
  const [recoveredMeta, setRecoveredMeta] = useState(null);
  const [style, setStyle] = useState(initialContent?.worksheetMeta?.style || generationSettings?.style || 'clean');
  const [layout, setLayout] = useState(initialContent?.worksheetMeta?.layout || 'standard');
  const [working, setWorking] = useState(initialContent?.worksheetMeta?.working || 'none');
  const metadata = React.useMemo(() => ({ ...initialContent?.worksheetMeta, ...recoveredMeta, ...(generationQuality?{quality:generationQuality}:{}), style, layout, working, settings: recoveredMeta?.settings || generationSettings || initialContent?.worksheetMeta?.settings || null, generationId: recoveredMeta?.generationId || generationId || initialContent?.worksheetMeta?.generationId || null }), [initialContent, recoveredMeta, style, layout, working, generationId, generationSettings, generationQuality]);
  const [layoutUpgraded, setLayoutUpgraded] = useState(false);
  const [overflow, setOverflow] = useState(false);
  const [contentReady, setContentReady] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentLatex, setCurrentLatex] = useState("");
  const [currentPos, setCurrentPos] = useState(null);
  const editorRootRef = useRef(null);

  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [feedbackShown, setFeedbackShown] = useState(false);
  const [isGenerationFeedbackOpen, setIsGenerationFeedbackOpen] = useState(false);

  const { user, aiAllowance } = useAuth();
  const regenerationAttempt = useRef(null);
  const appliedInitialContentRef = useRef(null);


  const [docReady, setDocReady] = useState(false);
  const lastAppliedKeyRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [fitPage, setFitPage] = useState(true);
  const scrollAreaRef = useRef(null);
  const prevZoomRef = useRef(1);
  const [helpOpen, setHelpOpen] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit,
      ExtendedTable.configure({
        resizable: true,
        lastColumnResizable: true,
        lastRowResizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
      TextStyle,
      MathInline,
      AnswerBox,
      Diagram,
      QuestionBlock,
      QuestionRow,
      WorksheetHeading,
      AnswerSection,
      FontSize,
      Color.configure({
        types: ['textStyle'],
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Subscript,
      Superscript,
      Underline,
      WorksheetPagination.configure({
        defaultPaperSize: 'A4',
        defaultPaperColour: '#ffffff',
        defaultPaperOrientation: 'portrait',
        defaultMarginConfig: {
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
        },
        defaultPageBorders: {
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
        },
        pageAmendmentOptions: {
          enableHeader: false,
          enableFooter: false,
        },
      }),
      PageNode,
      HeaderFooterNode,
      BodyNode,
    ],
    content: '',
  })


  useEffect(() => {
    // Reopening a saved worksheet supplies its own content and must not be
    // wiped by the fresh-generation reset below (this also matters because
    // React 18 StrictMode double-invokes mount effects in dev, which would
    // otherwise clear content the reopen effect had just set).
    if (initialContent) return;

    setDocReady(false);
    lastAppliedKeyRef.current = null;

    if (editor) {
      editor.commands.clearContent();
    }
  }, [template_id, initialContent, editor]);
  

  useEffect(() => {
    if (questions && questions.length > 0) {
      setDocReady(true);
      // Lets the onboarding pop-up wait until the teacher has had a worksheet.
      try {
        localStorage.setItem(FIRST_WORKSHEET_KEY, "true");
      } catch {
        // Storage unavailable (private mode): the pop-up just keeps waiting.
      }
    }
  }, [questions]);

  useEffect(() => {
    if (!editor || !docReady) return;
  
    const key = JSON.stringify({ template_id, questions });
    if (lastAppliedKeyRef.current === key) return;
  
    const doc = buildDocFromTemplate(template_id, questions, title);

    editor.commands.setContent(doc, false);
    lastAppliedKeyRef.current = key;
    setContentReady(true);


    // Show generation feedback toast 2 seconds after questions are generated
    if (questions && questions.length > 0) {
      const timer = setTimeout(() => {
        setIsGenerationFeedbackOpen(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [editor, template_id, questions, docReady, title]);

  useEffect(() => {
    if (!editor?.state?.doc || !contentReady) return;
    let heading;
    editor.state.doc.descendants((node, pos) => {
      if (!heading && node.type.name === 'worksheetHeading') heading = { node, pos };
    });
    if (heading && heading.node.textContent !== title) {
      editor.view.dispatch(editor.state.tr.insertText(title, heading.pos + 1, heading.pos + heading.node.nodeSize - 1));
    }
  }, [editor, title, contentReady]);

  // Reopening a saved worksheet: load its Tiptap JSON directly, skipping
  // the questions -> buildDocFromTemplate pipeline used for fresh generations.
  useEffect(() => {
    if (!editor || !initialContent) return;
    if (appliedInitialContentRef.current === worksheetId) return;

    editor.commands.setContent(initialContent, false);

    appliedInitialContentRef.current = worksheetId;
    setContentReady(true);
  }, [editor, initialContent, worksheetId]);


  React.useEffect(() => {
    if (!editor || !editorRootRef.current) return;

    const classifyTables = () => {
      const root = editorRootRef.current;
      if (!root) return;

      const tables = root.querySelectorAll("table");

      tables.forEach((table) => {
        const rows = table.querySelectorAll("tbody tr");
        if (rows.length !== 4) {
          table.removeAttribute("data-column-math");
          return;
        }

        // We expect 4 columns per row for your column problems
        const firstRowCells = rows[0].querySelectorAll("td, th");
        if (firstRowCells.length !== 4) {
          table.removeAttribute("data-column-math");
          return;
        }

        // If it looks like one of our 4x4 column-op tables, flag it
        table.setAttribute("data-column-math", "true");
      });
    };

    // Run when the editor updates
    editor.on("update", classifyTables);

    // Run once right away (after initial content)
    classifyTables();

    return () => {
      editor.off("update", classifyTables);
    };
  }, [editor, editorRootRef]);


  React.useEffect(() => {
    const handleEditMath = (e) => {
      setCurrentPos(e.detail.pos);
      setCurrentLatex(e.detail.latex);
      setModalOpen(true);
    };

    document.addEventListener("editMath", handleEditMath);
    return () => document.removeEventListener("editMath", handleEditMath);
  }, []);


  const handleSaveMath = (newLatex) => {
    if (editor && typeof currentPos === "number") {
      editor.chain()
        .focus()
        .setNodeSelection(currentPos)
        .updateAttributes("mathInline", { latex: newLatex })
        .run();
    }
  };

  useEffect(() => {
    const root = editorRootRef.current;
    if (!root) return;
    const report = event => setOverflow(event.detail);
    root.addEventListener('worksheet-overflow', report);
    return () => root.removeEventListener('worksheet-overflow', report);
  }, [editor]);

  useEffect(() => {
    if (!fitPage || !scrollAreaRef.current || typeof ResizeObserver === 'undefined') return;
    const fit = () => setZoom(Math.min(1, Math.max(0.25, (scrollAreaRef.current.clientWidth - 24) / 794)));
    const observer = new ResizeObserver(fit);
    observer.observe(scrollAreaRef.current);
    fit();
    return () => observer.disconnect();
  }, [fitPage]);

  const openPdfModal = () => {
    if (!contentReady || overflow) return;
    prevZoomRef.current = zoom;  // remember current zoom
    setZoom(1);                  // reset to 100%
    setPdfModalOpen(true);       // open modal (preview generation can start)
  };

  const draft = useWorksheetDraft({ editor, userId: user?.id, worksheetId, templateId: template_id,
    title, sourcePrompt, metadata, ready: contentReady, onPersisted: onWorksheetPersisted });
  useEffect(() => {
    // A recovery decision needs to be visible even when tools start collapsed.
    if (draft.recovery) setToolsExpanded(true);
  }, [draft.recovery]);
  const persistWorksheet = async () => draft.save();
  const handleManualSave = async () => {
    if (!contentReady) return;
    const result = await draft.save();
    if (result.ok && worksheetId && onWorksheetPersisted) onWorksheetPersisted(result.id);
  };
  const canRegenerate = template_id === 'exam-style' && Boolean(sourcePrompt || metadata.settings?.prompt);
  const saveStatusText = {
    idle: 'Draft', unsaved: 'Unsaved changes', saving: 'Saving…',
    saved: 'Saved to My Worksheets', error: 'Couldn’t save. Keep this page open and retry.',
  }[draft.status];
  const SaveStatusIcon = draft.status === 'saved' ? CloudDoneOutlined : draft.status === 'error' ? ErrorOutline : CloudUploadOutlined;
  const qualityNoticeKey = JSON.stringify([worksheetId, metadata.generationId, metadata.quality?.requestedCount, metadata.quality?.deliveredCount, metadata.quality?.omittedCount]);

  const handleRegenerate = async ({ raw, enriched, region, difficulty, topics, ...settings }) => {
    if (regenerating) return;
    setRegenerating(true);
    setGenerationStage('Saving your edits');
    setRegenerateError('');
    editor?.setEditable?.(false);
    try {
      const saved = await draft.save();
      if (!saved.ok) throw new Error('Save your current worksheet successfully before regenerating.');
      setGenerationStage('Generating questions');
      const request = { prompt: raw, region, difficulty, topics, ...settings };
      const fingerprint = JSON.stringify(request);
      if (regenerationAttempt.current?.fingerprint !== fingerprint) regenerationAttempt.current = { fingerprint, requestId: newRequestId() };
      const id = await regenerateWorksheet({ userId: user.id, request, title, style: request.style || style, layout, working, requestId: regenerationAttempt.current.requestId, worksheetId: saved.id, onStage:setGenerationStage, previousVersion: { title, sourcePrompt:sourcePrompt||metadata.settings?.prompt||null, content: { ...editor.getJSON(), worksheetMeta: { ...metadata, previousVersion: undefined } } } });
      if (onVersionCreated) onVersionCreated(id);
      else window.location.assign(`/worksheet/${id}`);
    } catch (error) {
      setRegenerateError(error.response?.data?.error || error.message || 'Could not generate another version. Please try again.');
    } finally {
      editor?.setEditable?.(true);
      setRegenerating(false);
    }
  };

  const undoRegeneration = async () => {
    const previous=metadata.previousVersion;
    if(!previous || regenerating)return;
    setRegenerating(true);setGenerationStage('Restoring your previous worksheet');setRegenerateError('');
    try {
      const {error}=await saveWorksheet({id:worksheetId,userId:user.id,templateId:template_id,title:previous.title,sourcePrompt:previous.sourcePrompt,content:previous.content});
      if(error)throw new Error('Could not restore the previous worksheet. Please retry.');
      window.location.reload();
    }catch(error){setRegenerateError(error.message);setRegenerating(false);}
  };
  const closePdfModal = (wasSuccessful = false) => {
    setPdfModalOpen(false);
    setZoom(prevZoomRef.current);

    if (wasSuccessful) {
      markGenerationExported(metadata.generationId);
      persistWorksheet();

      // Only show feedback modal if not already shown this session
      if (!feedbackShown) {
        // Delay opening feedback modal until after PDF modal closes
        setTimeout(() => {
          setIsFeedbackModalOpen(true);
          setFeedbackShown(true);
        }, 100); // Small delay allows PDF modal to fully unmount
      }
    }
  };


  return (
    <div className={`worksheet-workspace${showRegenerate?' regeneration-drawer-open':''}${regenerating?' workspace-busy':''}`} aria-busy={regenerating} data-style={style} data-layout={layout} data-working={working}>
      <section className="workspace-controls no-print" aria-label="Worksheet tools">
        <div className="workspace-header">
          <div className="workspace-title-area">
            <Tooltip title={saveStatusText} describeChild>
              <span className={`workspace-save-status workspace-save-status-${draft.status}`} role="img" aria-label={saveStatusText} tabIndex={0}>
                <SaveStatusIcon aria-hidden="true" />
              </span>
            </Tooltip>
            <input className="workspace-title-input" aria-label="Worksheet title" value={title} disabled={regenerating} onChange={e => setTitle(e.target.value)} />
            <span className="workspace-visually-hidden" role="status" aria-live="polite">{saveStatusText}</span>
          </div>
          <div className="workspace-header-actions">
            {draft.status !== 'saved' && <button className="workspace-button workspace-save-button" disabled={!contentReady || regenerating || Boolean(draft.recovery) || draft.status === 'saving'} onClick={handleManualSave}>
              <SaveOutlined aria-hidden="true" />
              {draft.status === 'error' ? 'Retry save' : draft.status === 'saving' ? 'Saving…' : worksheetId ? 'Save changes' : 'Save Worksheet'}
            </button>}
            {canRegenerate ? <button ref={regenerateToggleRef} className="workspace-button" aria-expanded={showRegenerate} aria-controls="worksheet-regenerate-panel" disabled={!contentReady || regenerating || Boolean(draft.recovery) || aiAllowance?.usage?.remaining === 0} onClick={() => { setShowRegenerate(value => !value); setFitPage(true); }}>
              <Refresh aria-hidden="true" />Regenerate
            </button> : <a className="workspace-button" href={`/create/${template_id}`}>Create another worksheet</a>}
            <button className="workspace-button workspace-download-button download_pdf_button" disabled={!contentReady || overflow || regenerating} onClick={openPdfModal}>
              <PictureAsPdf aria-hidden="true" />Preview &amp; download
            </button>
            <button ref={toolsToggleRef} className="workspace-tools-toggle" disabled={regenerating} aria-expanded={toolsExpanded} aria-controls="worksheet-tools-body" onClick={() => setToolsExpanded(value => !value)}>
              {toolsExpanded ? 'Hide tools' : 'Show tools'}{toolsExpanded ? <ExpandLess aria-hidden="true" /> : <ExpandMore aria-hidden="true" />}
            </button>
          </div>
        </div>
        <div id="worksheet-tools-body" className="workspace-tools-body" hidden={!toolsExpanded}>
        <div className="workspace-options">
          {sourcePrompt && <button className="workspace-link-button" onClick={() => setShowRegenerate(true)}>Original request</button>}
          {metadata.previousVersion && <button className="workspace-link-button" disabled={regenerating} onClick={undoRegeneration}>Undo regeneration</button>}
        </div>
      {draft.recovery && <div className="draft-recovery no-print" role="alert">
        A draft from your last visit is available.
        <button onClick={() => { setRecoveredMeta(draft.recovery.content.worksheetMeta || {}); setTitle(draft.recovery.title); setStyle(draft.recovery.content.worksheetMeta?.style || 'clean'); setLayout(draft.recovery.content.worksheetMeta?.layout || 'standard'); setWorking(draft.recovery.content.worksheetMeta?.working || 'none'); editor.commands.setContent(draft.recovery.content); draft.clearRecovery(); draft.changed(); }}>Restore draft</button>
        <button onClick={() => { draft.clearRecovery(); draft.changed(); }}>Use current version</button>
      </div>}
      <div className="editor-toolbar" inert={regenerating?'':undefined}>

        <MenuBar
          pageLayout={{ supportsStyle: template_id === 'exam-style' && (!initialContent || layoutUpgraded || JSON.stringify(initialContent).includes('questionBlock')), style, onStyle: setStyle, spacing: layout, working, disabled: regenerating,
            onSpacing: setLayout, onWorking: setWorking, onFit: () => setFitPage(true),
            canUpgrade: Boolean(initialContent && !layoutUpgraded && template_id === 'exam-style' && !JSON.stringify(initialContent).includes('questionBlock')),
            onUpgrade: () => { editor.commands.setContent(upgradeExamLayout(editor.getJSON())); setLayoutUpgraded(true); draft.changed(); } }}
          editor={editor}
          zoom={zoom}
          setZoom={value => { setFitPage(false); setZoom(value); }}
          onOpenHelp={() => setHelpOpen(true)}
        />
      </div>

      {metadata.quality?.omittedCount > 0 && dismissedQualityNotice !== qualityNoticeKey && <div className="worksheet-quality-note" role="status">
        <details>
          <summary>
            <WarningAmberOutlined aria-hidden="true" />
            <span className="worksheet-quality-message"><strong>{metadata.quality.deliveredCount} of {metadata.quality.requestedCount} questions created.</strong><span className="worksheet-quality-advice"> Review before using.</span></span>
            <ExpandMore className="worksheet-quality-chevron" aria-hidden="true" />
          </summary>
          <p>{metadata.quality.omittedCount === 1 ? 'One question was' : 'Some questions were'} left out during quality checks. Review this worksheet before using it{canRegenerate ? ', or regenerate to try for a complete set.' : '.'}</p>
        </details>
        <button type="button" className="worksheet-quality-dismiss" aria-label="Dismiss quality warning" onClick={() => { setDismissedQualityNotice(qualityNoticeKey); toolsToggleRef.current?.focus(); }}><Close aria-hidden="true" /></button>
      </div>}
        </div>
      </section>
      {canRegenerate && <aside className="regeneration-drawer no-print" id="worksheet-regenerate-panel" aria-labelledby="regeneration-drawer-title" hidden={!showRegenerate} ref={regeneratePanelRef} tabIndex={-1} onKeyDown={e => { if (e.key === 'Escape') { e.stopPropagation(); closeRegenerate(); } }}>
        <div className="regeneration-drawer-header"><div><h2 id="regeneration-drawer-title">Refine your worksheet</h2><p>Adjust the request, then generate a new version.</p></div><button type="button" className="workspace-button" onClick={closeRegenerate} aria-label="Collapse regeneration panel"><Close aria-hidden="true" /></button></div>
        <div className="regeneration-drawer-body">
          <details className="workspace-prompt"><summary>Original request</summary><p>{sourcePrompt || metadata.settings?.prompt}</p></details>
          <p className="regeneration-drawer-note">Your current worksheet stays safe until the new version is saved. You can undo the last regeneration.</p>
          <CustomPrompt initialText={sourcePrompt || metadata.settings?.prompt} initialRegion={metadata.settings?.region}
            initialDifficulty={metadata.settings?.difficulty} initialTopics={metadata.settings?.topics || []} initialSettings={{ ...metadata.settings, style }}
            compact showSuggestions={false} loading={regenerating} onGenerate={handleRegenerate} />
          {regenerating && <p role="status">{generationStage}…</p>}
          {regenerateError && <p role="alert">{regenerateError}</p>}
        </div>
      </aside>}
      <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/katex@0.16.9/dist/katex.min.css" />
      {overflow && <p role="alert">A question or table is taller than a page. Reduce its text size or working space before downloading.</p>}
      {/* Scroll only the editor pages */}
      <div className={`generation-stage${regenerating?' is-generating':''}`} aria-busy={regenerating}>
      {regenerating && <div className="generation-cover" role="status"><div className="generation-orbit"/><strong>{generationStage}</strong><p>Please keep this page open while we prepare your worksheet.</p><small>Your current worksheet stays safe until the replacement is saved.</small></div>}
      <div className="editor-scroll-area" ref={scrollAreaRef}>
        <div className="editor-zoom-layer" style={{ zoom }}>
          <div ref={editorRootRef}>
            <EditorContent editor={editor} />
          </div>
        </div>
      </div>

      </div>
      <MathEditModal
        isOpen={modalOpen}
        latex={currentLatex}
        onSave={handleSaveMath}
        onClose={() => setModalOpen(false)}
      />

      <PdfExportModal
        isOpen={pdfModalOpen}
        onClose={closePdfModal}
        editorRootRef={editorRootRef}
      />

<EditorHelpModal
  open={helpOpen}
  onClose={() => setHelpOpen(false)}
/>

<FeedbackModal
  isOpen={isFeedbackModalOpen}
  onClose={() => {
    setIsFeedbackModalOpen(false);

  }}
  template={template_id} // don't forget to pass this prop
/>

<GenerationFeedbackToast
  isOpen={isGenerationFeedbackOpen}
  onClose={() => setIsGenerationFeedbackOpen(false)}
  template={template_id}
  generationId={metadata.generationId}
/>
    </div>
  );
}
